import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthModal } from './components/AuthModal';
import { useAuthModal, AuthModalProvider } from './contexts/AuthContext';
import { UnauthenticatedHeader } from './components/ui/UnauthenticatedHeader';
import { Icons } from './components/ui/Icons';
import { Onboarding } from './components/Onboarding';
import { Dashboard } from './components/Dashboard';
import { PublicGallery } from './components/PublicGallery';
import { PublicCourseGallery } from './components/PublicCourseGallery';
import { CreatorLanding } from './components/CreatorLanding';
import { StudyViewerRoute } from './components/routes/StudyViewerRoute';
import { StudyEditorRoute } from './components/routes/StudyEditorRoute';
import { CourseViewerRoute } from './components/routes/CourseViewerRoute';
import { CourseEditorRoute } from './components/routes/CourseEditorRoute';
import { CoursePortal } from './components/CoursePortal';
import { AIUsageAdmin } from './components/AIUsageAdmin';
import { ResponsesAdmin } from './components/ResponsesAdmin';
import { MediaManager } from './components/MediaManager';
import { AnalyticsAdmin } from './components/AnalyticsAdmin';
import { NotificationSettingsAdmin } from './components/NotificationSettingsAdmin';
import { ContentManager } from './components/ContentManager';
import { SubscriptionAdmin } from './components/SubscriptionAdmin';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import {
  subscribeToAuthChanges,
  getUserProfile,
  subscribeToUserProfile,
  getOrganization,
  logout,
} from './services/firebase';
import { User } from 'firebase/auth';
import {
  UserProfile,
  Organization,
  ViewState,
  Course,
  Study,
} from './types';
import { formatAuthorName } from './utils/formatters';

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [viewState, setViewState] = useState<ViewState>(ViewState.AUTH);
  const [activeCourse, setActiveCourse] = useState<Course | null>(null);
  const [activeCourseId, setActiveCourseId] = useState<string | null>(null);
  const [courseStudies, setCourseStudies] = useState<Study[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAwardAlert, setNewAwardAlert] = useState<any[] | null>(null);
  const { isAuthModalOpen, closeAuthModal, openAuthModal, authModalInitialState, authModalInitialAccountType } = useAuthModal();
  const [dashboardActiveTab, setDashboardActiveTab] = useState<'browse' | 'profile' | 'members' | 'courses'>('browse');
  const [featuredCourse, setFeaturedCourse] = useState<Course | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const orgCode = searchParams.get('orgCode');
    if (orgCode) {
      localStorage.setItem('orgCode', orgCode);
    }
    
    // Fetch a random featured course
    const loadFeaturedCourse = async () => {
      try {
        const { getPublicCourses } = await import('./services/firebase');
        const courses = await getPublicCourses();
        if (courses.length > 0) {
          const randomIndex = Math.floor(Math.random() * courses.length);
          setFeaturedCourse(courses[randomIndex]);
        }
      } catch (error) {
        console.error("Failed to load featured course", error);
      }
    };
    loadFeaturedCourse();
  }, []);

  useEffect(() => {
    let profileUnsubscribe: (() => void) | null = null;
    let orgCreationTimeout: NodeJS.Timeout | null = null;

    const authUnsubscribe = subscribeToAuthChanges(async (firebaseUser) => {
      setUser(firebaseUser);

      // Clean up previous profile listener if any
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }

      // Clear any existing timeout
      if (orgCreationTimeout) {
        clearTimeout(orgCreationTimeout);
        orgCreationTimeout = null;
      }

      if (firebaseUser) {
        // Reset state for new user
        setUserProfile(null);
        setOrganization(null);
        setLoading(true);

        // Store userId in localStorage for route components
        localStorage.setItem('userId', firebaseUser.uid);
        closeAuthModal(); // Close modal if open on success

        // Safety timeout: if creatingOrganization flag is still set after 15 seconds, clear it
        const isCreatingOrg = sessionStorage.getItem('creatingOrganization') === 'true';
        if (isCreatingOrg) {
          orgCreationTimeout = setTimeout(() => {
            console.warn('Organization creation timeout - clearing flag and showing onboarding');
            sessionStorage.removeItem('creatingOrganization');
            // Force a re-check by updating a state value
            setLoading(false);
          }, 15000);
        }

        // Subscribe to real-time profile changes
        // This automatically handles the case where orgId is added after signup
        profileUnsubscribe = subscribeToUserProfile(firebaseUser.uid, async (profile) => {
          const isCreatingOrg = sessionStorage.getItem('creatingOrganization') === 'true';

          if (profile) {
            setUserProfile(profile);
            // Check if user is part of an organization
            if (profile.orgId) {
              // Clear the creatingOrganization flag if it was set
              sessionStorage.removeItem('creatingOrganization');
              // Clear the timeout since org was successfully created
              if (orgCreationTimeout) {
                clearTimeout(orgCreationTimeout);
                orgCreationTimeout = null;
              }
              const org = await getOrganization(profile.orgId);
              setOrganization(org);

              // Wait for React to process the organization state update
              // before transitioning to dashboard
              await new Promise(resolve => setTimeout(resolve, 100));

              // If we were in onboarding (or initial load), go to dashboard
              setViewState(prev => prev === ViewState.AUTH || prev === ViewState.ONBOARDING ? ViewState.DASHBOARD : prev);
              setLoading(false);
            } else if (!isCreatingOrg) {
              // Only show onboarding if we're not creating an organization
              setOrganization(null);
              setViewState(ViewState.ONBOARDING);
              setLoading(false);
            }
            // If isCreatingOrg is true and no orgId yet, keep loading state active
          } else {
             // Create partial profile for onboarding
             const newProfile = {
               uid: firebaseUser.uid,
               displayName: firebaseUser.displayName || 'User',
               email: firebaseUser.email,
               photoURL: firebaseUser.photoURL,
               completedStudyIds: [],
               studyProgress: {},
               awards: [],
               xp: 0,
               isAdmin: false
             };
             setUserProfile(newProfile);

             // Only show onboarding if we're not creating an organization
             if (!isCreatingOrg) {
               setViewState(ViewState.ONBOARDING);
               setLoading(false);
             }
             // If isCreatingOrg is true, keep loading state active
          }
        });

      } else {
        // Clear userId from localStorage on logout
        localStorage.removeItem('userId');
        // Clear any pending organization creation flag
        sessionStorage.removeItem('creatingOrganization');
        setUserProfile(null);
        setOrganization(null);
        setViewState(ViewState.AUTH);
        setLoading(false);
      }
    });

    return () => {
      authUnsubscribe();
      if (profileUnsubscribe) profileUnsubscribe();
      if (orgCreationTimeout) clearTimeout(orgCreationTimeout);
    };
  }, []);

  // Sync viewState with URL on auth state changes
  // IMPORTANT: This must be before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (!loading) {
      const currentPath = location.pathname;

      if (viewState === ViewState.AUTH) {
        // Only redirect to home if on a protected route
        const protectedRoutes = ['/dashboard', '/onboarding'];
        if (protectedRoutes.some(route => currentPath.startsWith(route))) {
          navigate('/');
        }
        // Allow access to public routes: /, /teach, /gallery, /courses/gallery, /study/*, /course/*
      } else if (viewState === ViewState.DASHBOARD) {
        // Only navigate to dashboard if organization data is ready AND not already on a valid route
        if (organization && (currentPath === '/' || currentPath === '/onboarding' || currentPath === '/teach')) {
          navigate('/dashboard');
        }
      } else if (viewState === ViewState.ONBOARDING) {
        // Only navigate to onboarding if not already there
        if (currentPath !== '/onboarding') {
          navigate('/onboarding');
        }
      } else if (viewState === ViewState.PUBLIC_GALLERY) {
        navigate('/gallery');
      }
    }
  }, [viewState, loading, location.pathname, organization]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <Icons.Loader className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  // Award Modal
  const renderAwardModal = () => {
    if (!newAwardAlert || newAwardAlert.length === 0) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl transform scale-100 animate-bounce-in">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-600">
            <Icons.Award className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">New Award Unlocked!</h2>
          <p className="text-lg font-medium text-primary mb-1">{newAwardAlert[0].name}</p>
          <p className="text-gray-500 mb-6">{newAwardAlert[0].description}</p>
          <button
            onClick={() => setNewAwardAlert(null)}
            className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-900 transition-colors"
          >
            Claim Award
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderAwardModal()}
      {isAuthModalOpen && (
        <AuthModal
          onClose={closeAuthModal}
          onSuccess={() => {
            closeAuthModal();
          }}
          initialState={authModalInitialState}
          initialAccountType={authModalInitialAccountType}
        />
      )}
      {!user && <UnauthenticatedHeader />}

      <Routes>
        {/* Auth/Landing Route */}
        <Route path="/" element={
          viewState === ViewState.AUTH ? (
            <div className="min-h-screen bg-white font-sans text-gray-900 overflow-x-hidden">
              {/* Hero Section */}
              <section className="relative overflow-hidden pt-4 pb-0 lg:pt-8 lg:pb-8 px-6">
                 <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
                    {/* Text Content */}
                    <div className="text-center lg:text-left">
                      <h1 className="text-5xl md:text-7xl font-serif font-bold text-gray-900 mb-6 leading-tight tracking-tight animate-fade-in-up delay-100">
                         Master the Scripture <br/>
                         <span className="text-secondary relative inline-block text-shadow-sm">
                           One Study at a Time
                         </span>
                      </h1>
                      <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up delay-200">
                         Explore interactive bible studies, track your progress, and deepen your understanding of the Word. Join a community of learners today.
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 animate-fade-in-up delay-300">
                         <button
                           onClick={() => openAuthModal('signup')}
                           className="w-full sm:w-auto px-8 py-4 bg-secondary text-white text-lg font-bold rounded-xl shadow-xl hover:bg-blue-600 transition-all hover:-translate-y-1 flex items-center justify-center gap-2 group"
                         >
                           Start Your Journey <Icons.Next className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                         </button>
                         <button
                           onClick={() => {
                             const element = document.getElementById('featured-courses');
                             element?.scrollIntoView({ behavior: 'smooth' });
                           }}
                           className="w-full sm:w-auto px-8 py-4 bg-white text-primary text-lg font-bold rounded-xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 flex items-center justify-center gap-2 border-2 border-primary group"
                         >
                           <Icons.BookOpen className="w-5 h-5" />
                           Explore Library
                         </button>
                      </div>
                    </div>

                    {/* Hero Visual - Daily Course Card */}
                    <div className="hidden lg:block relative animate-fade-in-up delay-200">
                      <div className="absolute inset-0 bg-blue-200 rounded-3xl transform rotate-3 scale-105 opacity-50 blur-lg"></div>
                      <div className="relative bg-white p-6 rounded-3xl shadow-2xl border border-gray-100 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                        {/* Featured Course Cover Image */}
                        {featuredCourse ? (
                          <>
                            <div className="h-48 rounded-2xl mb-6 flex items-center justify-center relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-500">
                              {featuredCourse.coverImage ? (
                                <img
                                  src={featuredCourse.coverImage}
                                  alt={featuredCourse.title}
                                  className="w-full h-full object-cover"
                                  style={{
                                    objectPosition: featuredCourse.coverImagePosition
                                      ? `${featuredCourse.coverImagePosition.x}% ${featuredCourse.coverImagePosition.y}%`
                                      : 'center'
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                                   <Icons.BookOpen className="w-20 h-20 text-white drop-shadow-lg opacity-80" />
                                </div>
                              )}

                              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold border border-white/30 shadow-sm z-10">
                                Featured Course
                              </div>
                            </div>

                            <div className="space-y-4">
                               <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                                  {featuredCourse.tags && featuredCourse.tags.length > 0 && (
                                    <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md capitalize">{featuredCourse.tags[0]}</span>
                                  )}
                                  <span>•</span>
                                  <span>{featuredCourse.studyIds?.length || 0} studies</span>
                               </div>
                               <h3 className="text-2xl font-bold text-gray-900 font-serif line-clamp-1">{featuredCourse.title}</h3>
                               <p className="text-gray-600 line-clamp-2">
                                 {featuredCourse.description || "Start your daily spiritual journey with this featured course."}
                               </p>

                               <div className="pt-4 border-t border-gray-100">
                                  <button
                                    onClick={() => navigate(`/course/${featuredCourse.id}`)}
                                    className="text-primary font-bold text-sm hover:underline flex items-center gap-1"
                                  >
                                    Start Course <Icons.Next className="w-4 h-4" />
                                  </button>
                               </div>
                            </div>
                          </>
                        ) : (
                          // Loading State Placeholder
                          <div className="animate-pulse">
                             <div className="h-48 bg-gray-200 rounded-2xl mb-6"></div>
                             <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                             <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                             <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                          </div>
                        )}
                      </div>
                    </div>
                 </div>

                 {/* Decorative Elements - Cool Tones */}
                 <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl -z-10 opacity-40 pointer-events-none">
                    <div className="absolute top-20 left-0 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
                    <div className="absolute top-20 right-0 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
                 </div>
              </section>

              {/* Featured Courses Section */}
              <section id="featured-courses" className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="flex justify-between items-end mb-8">
                    <div>
                      <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2">Featured Courses</h2>
                      <p className="text-gray-600">Comprehensive series to guide your study.</p>
                    </div>
                    <button 
                      onClick={() => navigate('/courses/gallery')}
                      className="text-primary font-semibold hover:underline hidden md:block"
                    >
                      View All Courses
                    </button>
                  </div>
                  <PublicCourseGallery 
                    embedded={true}
                    onCourseSelect={async (course) => {
                      navigate(`/course/${course.id}`);
                    }}
                  />
                  <div className="mt-8 text-center md:hidden">
                    <button 
                      onClick={() => navigate('/courses/gallery')}
                      className="text-primary font-semibold hover:underline"
                    >
                      View All Courses
                    </button>
                  </div>
                </div>
              </section>

               {/* Why Sign Up Section */}
              <section className="py-16 bg-gray-50 px-6 overflow-hidden">
                 <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                    <div>
                      <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">Why Create an Account?</h2>
                      <p className="text-gray-600 text-lg mb-12">Enhance your study experience with these powerful features designed to help you grow.</p>

                      <div className="space-y-8">
                         <div className="flex gap-4">
                            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center text-sky-700 flex-shrink-0">
                               <Icons.Award className="w-6 h-6" />
                            </div>
                            <div>
                               <h3 className="text-xl font-bold mb-2">Track Your Progress</h3>
                               <p className="text-gray-600">Save your spot in studies, earn XP, and unlock exclusive badges as you complete milestones.</p>
                            </div>
                         </div>

                         <div className="flex gap-4">
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-green-700 flex-shrink-0">
                               <Icons.Quiz className="w-6 h-6" />
                            </div>
                            <div>
                               <h3 className="text-xl font-bold mb-2">Interactive Quizzes</h3>
                               <p className="text-gray-600">Test your knowledge and reinforce your learning with built-in quizzes and reflections.</p>
                            </div>
                         </div>

                         <div className="flex gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-700 flex-shrink-0">
                               <Icons.BookOpen className="w-6 h-6" />
                            </div>
                            <div>
                               <h3 className="text-xl font-bold mb-2">Personalized Library</h3>
                               <p className="text-gray-600">Curate your own list of favorite studies, resume where you left off, and explore recommendations.</p>
                            </div>
                         </div>
                      </div>

                      <div className="mt-12">
                        <button
                           onClick={() => openAuthModal('signup')}
                           className="px-8 py-4 bg-primary text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-900 transition-all hover:-translate-y-1"
                         >
                           Create Free Account
                         </button>
                      </div>
                    </div>

                    {/* Gamification Visual */}
                    <div className="relative">
                       <div className="absolute inset-0 bg-gradient-to-tr from-blue-200 to-indigo-100 rounded-full opacity-20 filter blur-3xl"></div>
                       <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 relative">
                          <div className="flex items-center justify-between mb-8">
                             <div>
                                <div className="text-sm text-gray-500 font-bold uppercase tracking-wider mb-1">Your Profile</div>
                                <div className="text-2xl font-bold text-gray-900">Bible Scholar</div>
                             </div>
                             <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center">
                                <Icons.User className="w-6 h-6 text-gray-400" />
                             </div>
                          </div>

                          {/* XP Bar */}
                          <div className="mb-8">
                             <div className="flex justify-between text-sm font-bold mb-2">
                                <span className="text-primary">Level 5</span>
                                <span className="text-gray-500">2,450 / 3,000 XP</span>
                             </div>
                             <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 w-[75%] rounded-full"></div>
                             </div>
                          </div>

                          {/* Badges */}
                          <div>
                             <div className="text-sm text-gray-500 font-bold uppercase tracking-wider mb-4">Recent Achievements</div>
                             <div className="flex gap-4">
                                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex flex-col items-center justify-center text-xs font-bold text-blue-700 border border-blue-100">
                                   <Icons.BookOpen className="w-6 h-6 mb-1 text-blue-500" />
                                   Reader
                                </div>
                                <div className="w-16 h-16 bg-green-50 rounded-2xl flex flex-col items-center justify-center text-xs font-bold text-green-700 border border-green-100">
                                   <Icons.Check className="w-6 h-6 mb-1 text-green-500" />
                                   First
                                </div>
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex flex-col items-center justify-center text-xs font-bold text-gray-400 border border-gray-100 border-dashed">
                                   <Icons.Lock className="w-5 h-5 mb-1" />
                                   ???
                                </div>
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex flex-col items-center justify-center text-xs font-bold text-gray-400 border border-gray-100 border-dashed">
                                   <Icons.Lock className="w-5 h-5 mb-1" />
                                   ???
                                </div>
                             </div>
                          </div>
                       </div>
                    </div>
                 </div>
              </section>

              {/* Featured Studies Section */}
              <section className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                  <div className="flex justify-between items-end mb-8">
                    <div>
                      <h2 className="text-3xl font-serif font-bold text-gray-900 mb-2">Latest Studies</h2>
                      <p className="text-gray-600">Dive into individual topics.</p>
                    </div>
                    <button 
                      onClick={() => navigate('/gallery')}
                      className="text-primary font-semibold hover:underline hidden md:block"
                    >
                      View All Studies
                    </button>
                  </div>
                  <PublicGallery 
                    embedded={true}
                    onStudySelect={async (study) => {
                      navigate(`/study/${study.id}`);
                    }}
                  />
                   <div className="mt-8 text-center md:hidden">
                    <button 
                      onClick={() => navigate('/gallery')}
                      className="text-primary font-semibold hover:underline"
                    >
                      View All Studies
                    </button>
                  </div>
                </div>
              </section>

              {/* Shared Footer */}
              <footer className="bg-gray-900 text-white py-12 px-6">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="flex items-center gap-3">
                       <div className="bg-gray-800 p-2 rounded-lg">
                          <Icons.BookOpen className="w-6 h-6 text-white" />
                       </div>
                       <span className="font-serif font-bold text-xl">Bible Aware</span>
                    </div>

                    <div className="flex flex-wrap justify-center gap-8 text-sm font-medium text-gray-400">
                       <button onClick={() => navigate('/teach')} className="hover:text-white transition-colors">Are you a Teacher?</button>
                       <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
                    </div>

                    <div className="text-gray-500 text-sm">
                       © {new Date().getFullYear()} Bible Aware.
                    </div>
                 </div>
              </footer>
            </div>
          ) : null
        } />
        <Route path="/teach" element={<CreatorLanding />} />

        {/* Onboarding Route */}
        <Route path="/onboarding" element={
          viewState === ViewState.ONBOARDING && userProfile ? (
            <Onboarding
              userId={userProfile.uid}
              userName={userProfile.displayName || 'Friend'}
              user={userProfile}
              onLogout={logout}
              onComplete={() => {
                 setViewState(ViewState.DASHBOARD);
                 navigate('/dashboard');
              }}
              onNavigate={(tab) => {
                setDashboardActiveTab(tab as any);
                navigate('/dashboard');
              }}
            />
          ) : null
        } />

        {/* Dashboard Route */}
        <Route path="/dashboard" element={
          viewState === ViewState.DASHBOARD && userProfile ? (
            <Dashboard
              user={userProfile}
              organization={organization}
              onLogout={logout}
              initialActiveTab={dashboardActiveTab}
              onCreateStudy={() => {
                navigate('/study/new/edit');
              }}
              onTakeStudy={(study) => {
                navigate(`/study/${study.id}`);
              }}
              onEditStudy={(study) => {
                navigate(`/study/${study.id}/edit`);
              }}
              onCreateCourse={() => {
                setActiveCourse(null);
                navigate('/course/new/edit');
              }}
              onEditCourse={(course) => {
                navigate(`/course/${course.id}/edit`);
              }}
              onViewCourse={async (courseId) => {
                navigate(`/course/${courseId}`);
              }}
            />
          ) : null
        } />

        {/* Public Gallery Route */}
        <Route path="/gallery" element={
          <PublicGallery
            onStudySelect={async (study) => {
              navigate(`/study/${study.id}`);
            }}
          />
        } />

        {/* Public Course Gallery Route */}
        <Route path="/courses/gallery" element={
          <PublicCourseGallery
            onCourseSelect={async (course) => {
              navigate(`/course/${course.id}`);
            }}
          />
        } />

        {/* Study Viewer Routes */}
        <Route path="/study/:studyId" element={<StudyViewerRoute />} />

        {/* Study Editor Routes */}
        <Route path="/study/:studyId/edit" element={<StudyEditorRoute />} />

        {/* Course Portal Route - Focused learning experience */}
        <Route path="/learn/:courseId" element={
          <CoursePortal
            user={userProfile}
            organization={organization}
          />
        } />

        {/* Course Viewer Route */}
        <Route path="/course/:courseId" element={<CourseViewerRoute />} />

        {/* Course Editor Routes */}
        <Route path="/course/:courseId/edit" element={<CourseEditorRoute />} />

        {/* AI Usage Admin Route */}
        <Route path="/admin/ai-usage" element={
          userProfile ? (
            <AIUsageAdmin
              user={userProfile}
              organization={organization}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Responses Admin Route (Questions + CTA Responses) */}
        <Route path="/admin/responses" element={
          userProfile?.uid ? (
            <ResponsesAdmin
              userId={userProfile.uid}
              user={userProfile}
              organization={organization}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Media Manager Route */}
        <Route path="/admin/media" element={
          userProfile ? (
            <MediaManager
              user={userProfile}
              organization={organization}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Analytics Admin Route */}
        <Route path="/admin/analytics" element={
          userProfile?.isAdmin ? (
            <AnalyticsAdmin
              user={userProfile}
              organization={organization}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Notification Settings Route */}
        <Route path="/admin/notifications" element={
          userProfile?.isAdmin ? (
            <NotificationSettingsAdmin
              user={userProfile}
              organization={organization}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Content Manager Route */}
        <Route path="/manage" element={
          userProfile?.isAdmin ? (
            <ContentManager
              user={userProfile}
              onLogout={logout}
              onCreateStudy={() => navigate('/study/new/edit')}
              onEditStudy={(study) => navigate(`/study/${study.id}/edit`)}
              onCreateCourse={() => navigate('/course/new/edit')}
              onEditCourse={(course) => navigate(`/course/${course.id}/edit`)}
              organization={organization}
            />
          ) : null
        } />

        {/* Secret Subscription Admin Route - only accessible by jonstewart24@gmail.com */}
        <Route path="/admin/subscriptions" element={
          userProfile ? (
            <SubscriptionAdmin
              user={userProfile}
              onLogout={logout}
            />
          ) : null
        } />

        {/* Privacy Policy - Public */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthModalProvider>
      <AppContent />
    </AuthModalProvider>
  );
}